import { css } from '@emotion/react';
import NvUserAvatar from 'components/nv-user-avatar';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { useMemo } from 'react';
import { addAlertMessage } from 'redux/actions/alert-messages';
import NvCheckbox from 'shared/components/inputs/nv-checkbox';
import NvFlyoutModal, { ModalType } from 'shared/components/nv-flyout-modal';
import { useAppDispatch } from 'redux/store';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { gray5, warning, white } from 'styles/global_defaults/colors';
import { doubleSpacing, halfSpacing, largeSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { config } from '@config/pendo.config.json';
import { RootState, WaitlistEntry as WaitlistSchemaEntry } from 'redux/schemas';
import { removeEnrolledLearnersFromWaitlist } from 'redux/actions/enrollment-waitlist';

type WaitlistPayload = {
  catalogId: string;
  users: {
    firstName: string;
    lastName: string;
    email: string;
  }[];
  courseRoleId: string | null;
}

type WaitlistSaveResult = {
  newlyEnrolled?: {
    email: string;
  }[];
};

type WaitlistEntry = WaitlistSchemaEntry & {
  selected: boolean;
};

export type WaitlistFlyoutProps = {
  onClose: () => void;
  onSave: (payload: WaitlistPayload) => Promise<WaitlistSaveResult | undefined>;
};

const titleHeight = 60;
const waitlistHeaderHeight = 90;
const footerHeight = 105;

const waitlistFlyoutStyles = css`
  &.waitlist-flyout {
    .modal-header {
      background-color: ${white};
      border-color: ${gray5};
    }
    input {
      padding-left: ${halfSpacing}px;
      &:checked + label:before,
      &.indeterminate + label:before {
        color: ${white};
        background-color: ${warning};
        border-color: ${warning};
      }
    }
    .waitlist-headers {
      height: ${waitlistHeaderHeight}px;
      padding: ${standardSpacing}px ${largeSpacing}px 0;
      .select-all {
        padding: 0 ${halfSpacing}px ${standardSpacing}px ${halfSpacing}px;
      }
      .headers {
        justify-content: space-between;
        padding-bottom: 1px;
        .name-and-email {
          padding-left: ${doubleSpacing}px;
        }
        .joined-waitlist-on {
          padding-right: ${standardSpacing}px;
        }
      }
      .separator {
        margin-bottom: 0;
      }
    }
    .modal-body {
      max-height: unset;
      height: calc(100vh - ${titleHeight + footerHeight + waitlistHeaderHeight}px);
      padding: 0 ${largeSpacing}px ${standardSpacing}px ${largeSpacing}px;
      .learners-selection {
        min-height: 200px;
        .waitlist-entry {
          height: 80px;
          padding: 0 ${halfSpacing}px;
          justify-content: space-between;
          .info {
            padding-left: 2px;
          }
        }
      }
    }
    .modal-footer {
      height: ${footerHeight}px;
      align-content: center;
      position: absolute;
      bottom: 0;
      width: 100%;
    }
  }
`;

const WaitlistFlyout = (props: WaitlistFlyoutProps) => {
  const dispatch = useAppDispatch();
  const currentCourseWaitlist: WaitlistEntry[] = useSelector(
    (state: RootState) => state.app.currentCourseWaitlist ?? [],
  );
  const catalogId = useSelector((state: RootState) => state.app.currentCatalogId);
  const initialWaitlist: WaitlistEntry[] = currentCourseWaitlist.map((entry) => ({
    ...entry,
    selected: false,
  }));
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>(initialWaitlist);

  const selectLearner = (userId: number) => {
    const updatedWaitlist = waitlist.map((entry) => {
      if (entry.userId === userId) {
        return {
          ...entry,
          selected: !entry.selected,
        };
      }
      return entry;
    });
    setWaitlist(updatedWaitlist);
  };

  const getSelectedLearners = useMemo(() => waitlist.filter((entry) => entry.selected), [waitlist]);

  const toggleAllLearners = (selectAll: boolean) => {
    const updatedWaitlist = waitlist.map((entry) => ({
      ...entry,
      selected: selectAll,
    }));
    setWaitlist(updatedWaitlist);
  };

  const handleOnSave = async () => {
    const selectedLearners = getSelectedLearners.map((learner) => ({
      firstName: learner.user?.firstName ?? '',
      lastName: learner.user?.lastName ?? '',
      email: learner.user?.email ?? '',
    }));

    const payload = {
      catalogId,
      users: selectedLearners,
      courseRoleId: null,
    };

    try {
      const result = await props.onSave(payload);
      const enrolledEmails = result?.newlyEnrolled?.map((learner) => learner.email) ?? [];
      const enrolledUserIds = waitlist
        .filter((entry) => {
          const email = entry.user?.email;
          return email && enrolledEmails.includes(email);
        })
        .map((entry) => entry.userId);

      if (enrolledUserIds.length === 0) {
        return;
      }

      dispatch(removeEnrolledLearnersFromWaitlist(enrolledUserIds));
      const updatedWaitlist = waitlist.filter((entry) => {
        const email = entry.user?.email;
        return !(email && enrolledEmails.includes(email));
      });
      setWaitlist(updatedWaitlist);
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
    }
  };

  return (
    <NvFlyoutModal
      type={ModalType.RIGHT}
      width={610}
      height='100%'
      onClose={props.onClose}
    >
      <div css={waitlistFlyoutStyles} className='waitlist-flyout'>
        <div className='modal-header'>
          <h4 className='modal-title condensed text-large-regular font-weight-bolder'>
            {t.USER_MANAGEMENT.ENROLLMENT.WAITLIST()}
          </h4>
        </div>
        <div className='waitlist-headers'>
          <div className='select-all'>
            <NvCheckbox
              label={t.USER_MANAGEMENT.ENROLLMENT.COUNTER(`${getSelectedLearners.length}`, `${waitlist.length}`)}
              name='all-learners'
              indeterminate={0 < getSelectedLearners?.length && getSelectedLearners?.length < waitlist.length}
              checked={getSelectedLearners?.length === waitlist.length}
              onChange={(event) => toggleAllLearners(event.currentTarget.checked)}
              dataQa={config.pendo.userManagement.waitlist.toggleSelectAll}
            />
          </div>
          <div className='headers d-flex text-small text-gray-3'>
            <div className='name-and-email'>
              {t.USER_MANAGEMENT.ENROLLMENT.NAME_AND_EMAIL()}
            </div>
            <div className='joined-waitlist-on'>
              {t.USER_MANAGEMENT.ENROLLMENT.JOINED_WAITLIST_ON()}
            </div>
          </div>
          <hr className='separator' />
        </div>
        <div className='modal-body'>
          <div className='learners-selection'>
            {waitlist?.map((learner) => (
              <div key={learner.userId} className='waitlist-entry d-flex align-items-center' data-qa={`learner_${learner.userId}`}>
                  <div className='learner-info d-flex align-items-center'>
                    <NvCheckbox
                      label=''
                      name={`learner_${learner.userId}`}
                      checked={learner.selected}
                      onChange={() => selectLearner(learner.userId)}
                      dataQa={config.pendo.userManagement.waitlist.toggleSelectLearner}
                    />
                    <NvUserAvatar
                      size='md'
                      user={learner.user}
                    />
                    <div className='info'>
                      <div className='name font-weight-bolder'>
                        {learner.user?.fullName}
                      </div>
                      <div className='position text-small text-gray-3'>
                        {learner.user?.email}
                      </div>
                    </div>
                  </div>
                  <div className='date text-small'>
                    {moment(learner.joinedAt).format('L LT')}
                  </div>
              </div>
            ))}
          </div>

        </div>
        <div className='modal-footer'>
          <button type='button' className='btn btn-default' onClick={props.onClose}>
            {t.FORM.CANCEL()}
          </button>
          <button
            type='button'
            className='btn btn-primary'
            onClick={handleOnSave}
            disabled={getSelectedLearners?.length === 0}
            data-qa={config.pendo.userManagement.waitlist.enrollSelected}
          >
            {t.FLYER.COURSE.ACTION.ENROLL()}
          </button>
        </div>
      </div>
    </NvFlyoutModal>
  );
};

export default WaitlistFlyout;
